import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useDialogCurrencyCompany } from "./columnsCurrencyCompany";
import { AlertColor } from "@mui/material";
import { Menuitem } from "../../../interfaces/Security/menu";
import GenericPromises from "../../../api/GenericPromises";
import { useCurrencies } from "../../../hooks/useCurrencies";
import { CompanyBankAccount } from "../../../interfaces/Security/companies";
import { Currency } from "../../../interfaces/Commons/currencies";
import { RectangularSkeleton } from "../../Commons/Skeleton";
import DataTable from "../GridTableMaterialUI/DataTable";

type TableApplyPaymentProps = {
    resourceScreen: Menuitem | undefined
    dataCurrencyTable: MyTableCurrencyBanks[];
    setDataCurrencyTable: Dispatch<SetStateAction<MyTableCurrencyBanks[]>>;
    setBackDropLoading: Dispatch<SetStateAction<boolean>>;
    setMessageSnack: Dispatch<SetStateAction<string>>;
    setUnauthorized: Dispatch<SetStateAction<boolean>>;
    showSnackBar: (message: string, severity: AlertColor) => void
}

interface MyTableCurrencyBanks {
    currency_id: number,
    currency_code: string,
    currency_description: string,
    balanceBankAccount: number,
    balanceAutorized: number,
    balanceFree: number,
    exchangeRate: number,
    active: boolean,
}

export const TableCurrencyCompany = ({
    resourceScreen,
    dataCurrencyTable,
    setDataCurrencyTable,
    setBackDropLoading,
    setMessageSnack,
    setUnauthorized,
    showSnackBar
}: TableApplyPaymentProps) => {
    const [isLoadingTableBankAccount, setIsLoadingTableBankAccount] = useState(false);
    const { columnsCurrency } = useDialogCurrencyCompany();
    const [columns, setColumns] = useState<GridColDef<MyTableCurrencyBanks>[]>(columnsCurrency);
    const { GenericGetResource, GenericGetResourceGeneric } = GenericPromises();
    const { GetExchangeRateFromCurrencyCode } = useCurrencies();

    const calculateBalanceCurrency = async (bankAccounts: CompanyBankAccount[], currencyCombo: Currency[]) => {
        setBackDropLoading(true);
        let mydataCurrencyTable: MyTableCurrencyBanks[] = await Promise.all(
            currencyCombo
                .map(async (element) => {
                    let exchangeRate = 1;
                    let date = new Date();
                    date.setDate(date.getDate() + 1);
                    let currencyId = currencyCombo.find((elementCurrency) => elementCurrency.currency_code === element.currency_code)?.currency_id ?? 0;

                    await GetExchangeRateFromCurrencyCode(currencyId, date).then((currency) => {
                        exchangeRate = (currency?.rate && currency?.rate > 0) ? currency?.rate : 1;
                    });

                    return {
                        currency_id: element.currency_id,
                        currency_code: element.currency_code,
                        currency_description: element.currency_description,
                        balanceBankAccount: 0,
                        balanceAutorized: 0,
                        balanceFree: 0,
                        exchangeRate: exchangeRate,
                        active: false
                    };
                })
        );

        bankAccounts.forEach(bankAccount => {
            if (bankAccount.is_active) {
                const currencyEntry = mydataCurrencyTable.find(
                    currency => currency.currency_code === bankAccount.currency_code
                );
                if (currencyEntry) {
                    currencyEntry.active = true
                    currencyEntry.balanceBankAccount += bankAccount.balance ?? 0;
                }
            }
        });

        setDataCurrencyTable(mydataCurrencyTable);
        setBackDropLoading(false);
    }

    useEffect(() => {
        const loadData = async () => {
            try {
                const [bankAccountsResponse, currenciesResponse] = await Promise.allSettled([
                    GenericGetResourceGeneric('/companybankaccounts', '/gcompanybankaccounts'),
                    GenericGetResource('/currencies'),
                ]);

                if (bankAccountsResponse.status === 'fulfilled' && currenciesResponse.status === 'fulfilled') {
                    await calculateBalanceCurrency(bankAccountsResponse.value.data.data, currenciesResponse.value.data.data);
                    setIsLoadingTableBankAccount(true);
                } else {
                    const error = new Error('Error fetching data');
                    throw error;
                }
            } catch (error: any) {
                setMessageSnack(error.message);
                showSnackBar(error.message, 'error');
                setUnauthorized(false);
            }
        }
        if (resourceScreen && resourceScreen.read) {
            loadData();
        }
    }, [resourceScreen]);

    return (
        <>
            {(!isLoadingTableBankAccount) ?
                <RectangularSkeleton height={75} />
                :
                <DataTable
                    columns={columns}
                    setColumns={setColumns}
                    data={dataCurrencyTable.filter(row => row.active === true)}
                    entityId={"currency_id"}
                    entity={`MyTableCurrencyBanks`}
                    preferences={{}}
                    namePreferences={"grid-currencybanks-columns"}
                    nameOrderColumns={"grid-currencybanks-columns"}
                    isChildren={true}
                    hideFooter={true}
                    hideShadow={true}
                    disableColumnMenu={true}
                />}
        </>
    )
}